import { useEffect, useState, useRef } from 'react';
import { api } from '../services/api';
import { PulseButton } from '@/components/ui/PulseButton';
import { COPY } from '@/lib/copyDictionary';
import { useGamenics, GamenicsEvent } from '@/hooks/useGamenics';
import { GamenicsManager } from '../services/gamenics';
import clsx from 'clsx';
import { Icon } from '../components/icons/Icon';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
// Removed Tabs, TabsContent, TabsList, TabsTrigger
import { ProfileEditModal } from '../components/ProfileEditModal';
import { ThemeCreationModal } from '../components/Admin/ThemeCreationModal';
import { useLocation } from 'wouter';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.esm.js';
import { motion, AnimatePresence } from 'framer-motion';
import ConfettiExplosion from 'react-confetti-explosion';
import { SlotCounter } from '@/components/ui/slot-counter';
import { useSoundEffects } from '@/hooks/useSoundEffects';
import badgesData from '../data/badges_master.json';
import { TipTrendChart, SupporterTrendChart, ProgressRing, HeatmapChart, TopSupporters } from '../components/Creator';
export const CreatorStudio = () => {
    const [user, setUser] = useState<any>(null);
    const [stats, setStats] = useState<any>(null);
    const [submissions, setSubmissions] = useState<any[]>([]);
    const [notifications, setNotifications] = useState<any[]>([]);

    const [loading, setLoading] = useState(true);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
    const [, setLocation] = useLocation();

    // Juice State
    const [isExploding, setIsExploding] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [badgeTab, setBadgeTab] = useState<'general' | 'script' | 'illustration' | 'voice'>('general');
    const { playLevelUp } = useSoundEffects();
    const { playSound, triggerCelebration } = useGamenics();

    const [proficiency, setProficiency] = useState<any>(null);
    const [earnedBadgeIds, setEarnedBadgeIds] = useState<Set<string>>(new Set());
    const [goals, setGoals] = useState<any[]>([]);

    // Phase 4: Insight Dashboard State
    const [tipTrendData, setTipTrendData] = useState<any[]>([]);
    const [tipSummary, setTipSummary] = useState<any>(null);
    const [supporterTrendData, setSupporterTrendData] = useState<any[]>([]);
    const [supporterSummary, setSupporterSummary] = useState<any>(null);
    const [heatmapData, setHeatmapData] = useState<any[]>([]);
    const [heatmapPeak, setHeatmapPeak] = useState<any>(null);
    const [topSupportersData, setTopSupportersData] = useState<any[]>([]);
    const [creatorGoals, setCreatorGoals] = useState<any[]>([]);
    const [newGoalText, setNewGoalText] = useState('');
    const [newGoalAmount, setNewGoalAmount] = useState(10000);

    // 習熟度ティア判定
    const profLevel = proficiency?.proficiency_level || 1;
    const userTier: 'beginner' | 'intermediate' | 'advanced' =
        profLevel >= 3 ? 'advanced' : profLevel >= 2 ? 'intermediate' : 'beginner';
    const tierLabel = { beginner: 'ビギナー', intermediate: 'クリエイター', advanced: 'マスター' }[userTier];
    const tierColor = { beginner: 'text-role-voice border-green-400', intermediate: 'text-primary border-ink', advanced: 'text-yellow-400 border-yellow-400' }[userTier];

    const fetchAll = async () => {
        const resProfile = await api.getUserProfile();
        if (resProfile.success) {
            setUser(resProfile.data.user);
            setStats(resProfile.data.stats);
        }

        const resSubs = await api.getUserSubmissions();
        setSubmissions(resSubs);

        const resProf = await api.getProficiency();
        if (resProf.success) {
            setProficiency(resProf.data);
        }

        // バッジ: まず評価を実行してから取得
        const evalRes = await api.evaluateBadges();
        if (evalRes.success && evalRes.data?.newly_unlocked?.length > 0) {
            // 新規獲得バッジがあれば演出（間 + 効果音 + パーティクル）
            triggerCelebration(GamenicsEvent.MILESTONE_10);
        }

        const resBadges = await api.getUserBadges();
        if (resBadges.success && resBadges.data) {
            setEarnedBadgeIds(new Set(resBadges.data));
        }

        const resGoals = await api.getUserGoals();
        if (resGoals.success) setGoals(resGoals.data || []);

        const resNotif = await api.getNotifications();
        if (resNotif.success) setNotifications(resNotif.data || []);

        await api.getUserLikes();

        // Phase 4: Insights Data
        const resTips = await api.getInsightsTips();
        if (resTips.success) {
            setTipTrendData(resTips.data || []);
            setTipSummary(resTips.summary || null);
        }
        const resSupporters = await api.getInsightsSupporters();
        if (resSupporters.success) {
            setSupporterTrendData(resSupporters.data || []);
            setSupporterSummary(resSupporters.summary || null);
        }
        const resHeatmap = await api.getInsightsHeatmap();
        if (resHeatmap.success) {
            setHeatmapData(resHeatmap.heatmap || []);
            setHeatmapPeak(resHeatmap.peak || null);
        }
        const resTop = await api.getInsightsTopSupporters();
        if (resTop.success) setTopSupportersData(resTop.data || []);
        const resCreatorGoals = await api.getCreatorGoals();
        if (resCreatorGoals.success) setCreatorGoals(resCreatorGoals.data || []);

        setLoading(false);
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const triggerConfetti = () => {
        setIsExploding(true);
        playSound(GamenicsEvent.TAP_CONFIRM);
        playLevelUp();
        setTimeout(() => setIsExploding(false), 3000);
    };

    if (loading) return <div className="h-screen flex items-center justify-center text-primary font-display text-xl animate-pulse">システム読み込み中...</div>;
    if (!user) return <div className="h-screen flex items-center justify-center">ログインしてください</div>;

    const tabs = [
        { id: 'overview', label: '概要', locked: false },
        { id: 'uploads', label: 'アップロード', locked: false },
        { id: 'badges', label: 'コレクション', locked: false },
        { id: 'analytics', label: userTier === 'beginner' ? '分析' : '分析', locked: userTier === 'beginner' },
    ];

    const lastSubDateStr = proficiency?.last_submission_at ? new Date(proficiency.last_submission_at).toISOString().split('T')[0] : null;
    const streakWarning = GamenicsManager.checkStreakWarning(proficiency?.streak_days || 0, lastSubDateStr);

    return (
        <div className="min-h-screen pt-8 pb-16 px-4 md:px-8 max-w-7xl mx-auto space-y-8">
            {/* 損失回避フレーミング警告バナー */}
            {streakWarning.shouldWarn && (
                <div className="bg-[#fdecec] border-2 border-red-400 text-red-700 p-3 rounded-lg flex items-center justify-center text-sm font-bold animate-pulse">
                    {streakWarning.message}
                </div>
            )}

            {/* 1. Hero / Gamification Header */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="md:col-span-2 border-ink bg-surface relative overflow-hidden">
                    <CardContent className="p-6 flex items-center gap-6 relative z-10">
                        <div className="relative">
                            <div className="w-24 h-24 rounded-full border-4 border-ink overflow-hidden ">
                                <img src={user.iconUrl} alt="Avatar" className="w-full h-full object-cover" />
                            </div>
                             <Badge variant="neon" className="absolute -bottom-2 -right-2 px-3 py-1 text-sm bg-surface border-pop text-pop">
                                 Lv. <SlotCounter value={proficiency?.proficiency_level || 1} />
                             </Badge>
                             <Badge variant="outline" className={`absolute -bottom-2 -left-2 px-2 py-0.5 text-[10px] bg-surface ${tierColor}`}>
                                 {tierLabel}
                             </Badge>
                        </div>
                        <div className="flex-1 space-y-2">
                             <div className="flex justify-between items-end">
                                 <h1 className="text-3xl font-display font-bold text-ink flex items-center gap-2">
                                     {user.displayName}
                                     {proficiency?.streak_days > 0 && (
                                         <Badge variant="outline" className="text-[#b8780b] border-ink bg-[#fff3e0] text-xs">
                                             {proficiency.streak_days}日連続
                                         </Badge>
                                     )}
                                 </h1>
                                 <span className={proficiency?.profile_completion_percent === 100 ? "text-xs font-mono text-primary font-bold" : "text-xs text-muted-foreground font-mono"}>
                                     {proficiency?.profile_completion_percent === 100 ? 'プロフィール完成！' : `プロファイル完成度: ${proficiency?.profile_completion_percent || 0}%`}
                                 </span>
                             </div>
                             {proficiency?.profile_completion_percent === 100 ? (
                                <div className="mt-2 bg-cream border border-ink/15 rounded-lg p-3">
                                    <div className="flex justify-between items-end mb-1">
                                        <span className="text-xs text-ink-2 font-bold">次のステップ：初セッションへの準備</span>
                                        <span className="text-[10px] text-muted-foreground font-mono">0%</span>
                                    </div>
                                    <Progress value={0} className="h-2 bg-cream-2" />
                                </div>
                             ) : (
                                <Progress value={proficiency?.profile_completion_percent || 0} className="h-3" />
                             )}
                            <div className="flex gap-2 pt-2 items-center flex-wrap">
                                <PulseButton size="sm" variant="outline" onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setIsEditOpen(true); }}>
                                    {COPY.editProfile}
                                </PulseButton>
                                <PulseButton size="sm" isPrimary onClick={triggerConfetti}>
                                    {COPY.claimBonus}
                                </PulseButton>
                                {isExploding && <div className="absolute left-1/2 top-1/2"><ConfettiExplosion force={0.8} duration={3000} particleCount={200} width={1600} colors={['#00f3ff', '#ff00ff', '#ffe600']} /></div>}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-ink/15 bg-surface">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg text-muted-foreground uppercase tracking-widest text-xs">エンゲージメント合計</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-bold text-pop">いいね獲得数</span>
                                <div className="text-3xl font-display font-bold"><SlotCounter value={stats.totalLikes} /></div>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-bold text-primary">総作品数</span>
                                <div className="text-3xl font-display font-bold"><SlotCounter value={stats.totalSubmissions} /></div>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-bold text-yellow-400">ADP残高</span>
                                <div className="text-3xl font-display font-bold"><SlotCounter value={stats.adp} /></div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* 2. Magic Motion Tabs */}
            <div className="space-y-6">
                <div className="flex gap-2 border-b border-ink/15 pb-1">
                    {tabs.map((tab) => (
                        <PulseButton
                            key={tab.id}
                            variant="ghost"
                            disabled={tab.locked}
                            onClick={() => { if (!tab.locked) { playSound(GamenicsEvent.TAP_CONFIRM); setActiveTab(tab.id); } }}
                            className={clsx("relative px-6 py-3 text-sm font-medium transition-colors", tab.locked ? 'text-ink-3 cursor-not-allowed opacity-50' : activeTab === tab.id ? 'text-ink' : 'text-muted-foreground hover:text-ink')}
                        >
                            {tab.label}
                            {activeTab === tab.id && (
                                <motion.div
                                    layoutId="underline"
                                    className="absolute bottom-0 left-0 right-0 h-1 bg-primary "
                                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                                />
                            )}
                        </PulseButton>
                    ))}
                </div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        initial={{ y: 10, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -10, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                    >
                        {activeTab === 'overview' && (
                            <div className="space-y-4">
                                {/* 習熟度別ヘルプセクション */}
                                {userTier === 'beginner' && (
                                    <Card className="border-2 border-role-voice bg-[#e3f7ef]">
                                        <CardContent className="p-5 flex items-start gap-4">
                                            <div>
                                                <h3 className="font-bold text-role-voice mb-1">ようこそ、ANOKOEの世界へ！</h3>
                                                <p className="text-sm text-ink-2">まずは作品を投稿してみましょう！台本・絵・声、どれでもOK。</p>
                                                <p className="text-xs text-ink-3 mt-2">投稿を重ねると「分析」タブが解放され、リスナーの反応を可視化できるようになります。</p>
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}
                                {userTier === 'intermediate' && (
                                    <Card className="border-ink bg-blue-950/20">
                                        <CardContent className="p-5 flex items-start gap-4">
                                            <div>
                                                <h3 className="font-bold text-primary mb-1">分析タブが使えるようになりました！</h3>
                                                <p className="text-sm text-ink-2">「分析」タブでリスナーがどこでリプレイしたかを確認してみましょう。</p>
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}
                                {/* advanced ではヘルプ非表示 */}

                                {/* ツァイガルニク効果: 未完了の直近目標 */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            あなたの直近目標
                                        </CardTitle>
                                        <CardDescription>目標を達成してコレクションや機能を開放しよう。</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        {goals.length === 0 ? (
                                            <div className="text-ink-3 text-sm italic">現在設定されている目標はありません。</div>
                                        ) : (
                                            <div className="space-y-3">
                                                {goals.map((g: any, i: number) => (
                                                    <div key={g.id} className="flex items-center justify-between bg-cream p-3 rounded-lg border border-ink/15" style={{ animationDelay: `${i * 100}ms` }}>
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-5 h-5 rounded border-2 border-primary flex items-center justify-center">
                                                                {g.is_completed ? <Icon name="check" size={12} className="text-primary" /> : null}
                                                            </div>
                                                            <span className={clsx("text-sm", g.is_completed ? "text-ink-3 line-through" : "text-ink")}>
                                                                {g.display_text}
                                                            </span>
                                                        </div>
                                                        <div className="text-xs font-mono text-primary">
                                                            {g.current_value} / {g.target_value}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardHeader>
                                        <CardTitle>最近のアクティビティ</CardTitle>
                                        <CardDescription>あなたの最近の活動とマイルストーンです。</CardDescription>
                                    </CardHeader>
                                    <CardContent className="h-[300px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-white/20">
                                        {notifications.length === 0 ? (
                                            <div className="h-full flex items-center justify-center text-muted-foreground border-2 border-dashed border-ink/15 rounded-lg">
                                                表示するアクティビティはありません。
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                {notifications.map((notif: any) => (
                                                    <div key={notif.id} className="flex items-start gap-4 border-b border-ink/15 pb-4 last:border-0">
                                                        <div className="w-10 h-10 rounded-full bg-cream-2 overflow-hidden flex-shrink-0 flex items-center justify-center text-xl">
                                                            {notif.actor_icon ? <img src={notif.actor_icon} alt="" className="w-full h-full object-cover" /> : (
                                                                notif.type === 'like' ? <Icon name="heart" size={16} /> : notif.type === 'comment' ? <Icon name="message" size={16} /> : notif.type === 'badge' ? <Icon name="award" size={16} /> : <Icon name="bell" size={16} />
                                                            )}
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="text-sm text-ink">
                                                                {notif.type === 'like' && <><span className="font-bold text-pop">{notif.actor_name}</span>さんがあなたの作品にいいねしました</>}
                                                                {notif.type === 'badge' && <>バッジを獲得しました！</>}
                                                                {notif.type !== 'like' && notif.type !== 'badge' && <>{notif.message}</>}
                                                            </p>
                                                            <p className="text-xs text-ink-3 mt-1">{new Date(notif.created_at).toLocaleString('ja-JP')}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>
                        )}

                        {activeTab === 'uploads' && (
                            <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <h2 className="text-xl font-bold text-ink">あなたの作品一覧</h2>
                                    <div className="flex gap-2">
                                        <PulseButton variant="outline" className="text-pop border-pop hover:bg-pop hover:text-ink" onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation('/'); }}>
                                            <Icon name="search" size={16} /> {COPY.searchThemes}
                                        </PulseButton>
                                        <PulseButton isPrimary onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setIsThemeModalOpen(true); }}>
                                            <Icon name="plus" size={16} /> {COPY.proposeTheme}
                                        </PulseButton>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {submissions.length === 0 && <div className="text-ink-3 col-span-full py-10 text-center">まだ作品がありません。</div>}
                                    {submissions.map((item: any) => (
                                        <SubmissionCard key={item.id} item={item} />
                                    ))}
                                </div>
                            </div>
                        )}

                        {activeTab === 'badges' && (
                            <Card className="border-ink/15 bg-surface">
                                <CardHeader>
                                    <CardTitle>あなたのコレクション</CardTitle>
                                    <CardDescription>ANOKOEの世界での軌跡。共創を通じて埋めていきましょう。</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex border-b border-ink/15 mb-6 overflow-x-auto whitespace-nowrap gap-2 pb-2">
                                        <PulseButton variant={badgeTab === 'general' ? 'default' : 'outline'} size="sm" onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setBadgeTab('general'); }} className={badgeTab === 'general' ? 'bg-primary text-ink' : ''}>全般</PulseButton>
                                        <PulseButton variant={badgeTab === 'script' ? 'default' : 'outline'} size="sm" onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setBadgeTab('script'); }} className={badgeTab === 'script' ? 'bg-primary text-ink' : ''}>台本</PulseButton>
                                        <PulseButton variant={badgeTab === 'illustration' ? 'default' : 'outline'} size="sm" onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setBadgeTab('illustration'); }} className={badgeTab === 'illustration' ? 'bg-primary text-ink' : ''}>絵</PulseButton>
                                        <PulseButton variant={badgeTab === 'voice' ? 'default' : 'outline'} size="sm" onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setBadgeTab('voice'); }} className={badgeTab === 'voice' ? 'bg-primary text-ink' : ''}>声</PulseButton>
                                    </div>
                                    
                                    {(() => {
                                        const roleBadges = badgesData.filter((b: any) => b.role === badgeTab).sort((a: any, b: any) => a.order - b.order);

                                        let firstMissingIdx = roleBadges.findIndex((b: any) => !earnedBadgeIds.has(b.id));
                                        if (firstMissingIdx === -1) firstMissingIdx = Math.max(0, roleBadges.length - 5);
                                        
                                        const blockStart = Math.floor(firstMissingIdx / 5) * 5;
                                        const blockBadges = roleBadges.slice(blockStart, blockStart + 5);

                                        return (
                                            <div className="w-full">
                                                <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                                                    {blockBadges.map((b: any, i: number) => {
                                                        const isUnlocked = earnedBadgeIds.has(b.id);
                                                        return (
                                                            <div key={b.id} className={clsx(
                                                                "flex flex-col items-center justify-center p-4 rounded-xl border text-center transition-all min-h-[120px]",
                                                                isUnlocked ? "bg-cream-2 border-primary " : "bg-surface border-ink/10 opacity-50 grayscale"
                                                            )}>
                                                                <div className="mb-2 flex justify-center">{isUnlocked ? <Icon name="star" size={30} className="text-role-script" /> : <Icon name="lock" size={28} className="text-ink-3" />}</div>
                                                                <div className="text-xs font-bold text-ink mb-1 line-clamp-1">{isUnlocked ? b.name : `??? (No.${blockStart + i + 1})`}</div>
                                                                <div className="text-[10px] text-ink-2 line-clamp-2 leading-tight">
                                                                    {isUnlocked ? b.description : `解放条件: ${b.description}`}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                <div className="text-center text-xs text-ink-3 mt-4 font-mono">
                                                    {blockStart + 1} 〜 {Math.min(blockStart + 5, roleBadges.length)} / {roleBadges.length}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </CardContent>
                            </Card>
                        )}

                        {activeTab === 'analytics' && (
                            <div className="space-y-6">
                                {/* サマリーカード */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="bg-cream-2 p-4 rounded-xl border border-ink/10">
                                        <div className="text-sm text-ink-2 mb-1">総作品数</div>
                                        <div className="text-3xl font-display font-bold text-primary">{submissions.length}</div>
                                    </div>
                                    <div className="bg-cream-2 p-4 rounded-xl border border-ink/10">
                                        <div className="text-sm text-ink-2 mb-1">総いいね獲得数</div>
                                        <div className="text-3xl font-display font-bold text-pop">
                                            {submissions.reduce((acc: number, sub: any) => acc + (sub.like_count || 0), 0)}
                                        </div>
                                    </div>
                                    <div className="bg-cream-2 p-4 rounded-xl border border-ink/10">
                                        <div className="text-sm text-ink-2 mb-1">サポーター数</div>
                                        <div className="text-3xl font-display font-bold text-role-voice">{supporterSummary?.totalSupporters || 0}</div>
                                    </div>
                                    <div className="bg-cream-2 p-4 rounded-xl border border-ink/10">
                                        <div className="text-sm text-ink-2 mb-1">累計応援額</div>
                                        <div className="text-3xl font-display font-bold text-yellow-400">¥{(tipSummary?.totalYen || 0).toLocaleString()}</div>
                                    </div>
                                </div>

                                {/* 目標達成メーター + チップ推移 */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="md:col-span-1 flex flex-col items-center justify-center">
                                        {creatorGoals.length > 0 ? (
                                            <ProgressRing
                                                current={tipSummary?.totalYen || 0}
                                                target={creatorGoals[0].target_amount}
                                                label={creatorGoals[0].display_text}
                                            />
                                        ) : (
                                            <div className="text-center space-y-4">
                                                <ProgressRing current={0} target={10000} label="目標未設定" />
                                                <div className="space-y-2">
                                                    <input
                                                        type="text"
                                                        value={newGoalText}
                                                        onChange={e => setNewGoalText(e.target.value)}
                                                        placeholder="目標名（例: 月間応援目標）"
                                                        className="w-full bg-surface border border-ink/15 rounded-lg px-3 py-2 text-sm text-ink placeholder-gray-500 focus:border-primary outline-none"
                                                    />
                                                    <input
                                                        type="number"
                                                        value={newGoalAmount}
                                                        onChange={e => setNewGoalAmount(Number(e.target.value))}
                                                        placeholder="目標金額"
                                                        className="w-full bg-surface border border-ink/15 rounded-lg px-3 py-2 text-sm text-ink placeholder-gray-500 focus:border-primary outline-none"
                                                    />
                                                    <PulseButton
                                                        variant="ghost"
                                                        className="w-full text-xs border border-primary text-primary rounded-lg px-3 py-2 hover:bg-primary/10"
                                                        onClick={async () => {
                                                            playSound(GamenicsEvent.TAP_CONFIRM);
                                                            await api.createCreatorGoal(newGoalAmount, newGoalText || '応援目標');
                                                            fetchAll();
                                                        }}
                                                    >
                                                        目標を設定する
                                                    </PulseButton>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="md:col-span-2">
                                        <TipTrendChart data={tipTrendData} />
                                    </div>
                                </div>

                                {/* 応援者推移 + トップサポーター */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <SupporterTrendChart data={supporterTrendData} />
                                    <TopSupporters data={topSupportersData} />
                                </div>

                                {/* ヒートマップ */}
                                <HeatmapChart data={heatmapData} />

                                {/* ピーク情報 */}
                                {heatmapPeak && (
                                    <div className="bg-cream-2 rounded-xl border border-ink/10 p-4 text-center">
                                        <span className="text-sm text-ink-2">応援が最も多い時間帯: </span>
                                        <span className="text-primary font-bold">
                                            {['日', '月', '火', '水', '木', '金', '土'][heatmapPeak.dayOfWeek]}曜 {heatmapPeak.hour}時
                                        </span>
                                        <span className="text-sm text-ink-2"> ({heatmapPeak.count}件)</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            {isEditOpen && (
                <ProfileEditModal
                    initialName={user.displayName}
                    initialIcon={user.iconUrl}
                    onClose={() => setIsEditOpen(false)}
                    onSave={async (name, icon) => {
                        // 失敗理由はモーダル内に出す（alert を使わない）。成功したときだけ閉じる
                        const res = await api.updateProfile({ displayName: name, iconUrl: icon });
                        if (res.success) fetchAll();
                        return res;
                    }}
                />
            )}

            {isThemeModalOpen && (
                <ThemeCreationModal
                    mode="user"
                    onClose={() => setIsThemeModalOpen(false)}
                    onSuccess={() => alert('新しいテーマの提案を送信しました！管理者の承認をお待ちください。')}
                />
            )}
        </div>
    );
};

const SubmissionCard = ({ item }: { item: any }) => (
    <Card className="overflow-hidden hover:border-primary transition-colors group bg-cream-2">
        <CardContent className="p-4 space-y-3">
            <div className="flex justify-between text-xs text-muted-foreground">
                <span>{new Date(item.createdAt * 1000).toLocaleDateString()}</span>
                <span className={item.status === 'RECRUITING' ? 'text-role-voice' : 'text-ink-3'}>{item.status}</span>
            </div>
            <div className="font-bold truncate">{item.themeTitle}</div>
            <audio controls src={item.audioUrl} className="w-full h-8 opacity-60 group-hover:opacity-100 transition-opacity" />
            <div className="flex items-center gap-2 text-xs font-bold text-pop">
                <span className="inline-flex items-center gap-1"><Icon name="heart" size={14} /> {item.likeCount}</span>
            </div>
        </CardContent>
    </Card>
);

export const WaveformDemo = ({ audioUrl }: { audioUrl?: string }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const wavesurferRef = useRef<WaveSurfer | null>(null);

    useEffect(() => {
        if (!containerRef.current) return;

        // Register Regions Plugin
        const ws = WaveSurfer.create({
            container: containerRef.current,
            waveColor: '#333',
            progressColor: '#00f3ff', // Neon Blue
            cursorColor: '#ff00ff', // Neon Pink
            barWidth: 2,
            barGap: 3,
            height: 100,
            plugins: [
                RegionsPlugin.create()
            ]
        });

        if (audioUrl) {
            ws.load(audioUrl);
        }
        // No fallback - audio URL is required

        // Add Heatmap Regions (if data exists)
        ws.on('ready', () => {
            // Future: Load real dropout/heatmap data from API
        });

        wavesurferRef.current = ws;

        return () => ws.destroy();
    }, [audioUrl]);

    return (
        <div className="space-y-4">
            <div ref={containerRef} className="w-full" id="waveform-container" />
            <p className="text-xs text-center text-muted-foreground">Heatmap indicates listener re-plays (Red = High Engagement)</p>
            <style>{`
                #waveform-container ::part(region) {
                    box-shadow: 0 0 10px rgba(255, 0, 85, 0.5);
                    border-radius: 2px;
                    backdrop-filter: blur(1px);
                }
            `}</style>
        </div>
    );
}
